import { useState, type ChangeEvent, type FocusEvent } from 'react';
import { useForm, FormProvider, type FieldErrors, type UseFormSetValue } from "react-hook-form";
import Text from "@/components/forms/text";
import Select from "@/components/forms/select";
import SimpleDatePicker from "@/components/forms/simple-date-picker";
import { useNavigate } from "react-router-dom";
import SelectContract from "@/components/shared/select-contract";
import Textarea from "@/components/forms/textarea";
import Files, { type FilesValue } from "@/components/forms/files";
import Warning from "@/pages/home/warning";
import { addDays, subDays } from 'date-fns';
import RadioGroup from "@/components/forms/radio-group";
import { getMotif } from "@/lib/motifs";
import Swal from "sweetalert2";
import FieldsResilRib from "@/pages/resiliation/formulaire-resil/fields-resil-rib";
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FileX, Phone, AlertTriangle, Send, ChevronLeft } from 'lucide-react';
import priseRDV from "@/assets/images/resiliation/prise-rdv.png";
import { useSelectedContract } from '@/hooks/api/use-contracts';
import { useBookingFidelisationUrl, useTerminate, type TerminateForm } from '@/hooks/api/use-terminate';
import { useWhitelabel } from '@/hooks/api/use-whitelabel';
import { getContractGarantiesOptions, isNVEIContract, isVAEContract } from '@/lib/contracts';
import { DATE_ISO, diffInCalendarDays, formatDate, todayIso, toDate } from '@/lib/dates';
import { sanitizeHtml } from '@/lib/notify';
import { isHttpUrl } from '@/lib/utils';
import type { Accident } from '@/types/api/accident';
import type { Contract, Garantie } from '@/types/api/contract';

/** Valeurs du formulaire de résiliation / renonciation. */
export interface ResilValues {
    police?: string;
    adhesion?: unknown;
    prenom?: string;
    nom?: string;
    email?: string;
    files: FilesValue;
    motif?: string;
    date_evenement?: string | Date | null;
    action?: string;
    motif_refus?: string;
    date_ag_res?: string;
    susp_res?: string;
    recontact?: string;
    commentaire?: string;
}

type PostTerminate = (values: ResilValues, successFunction: () => void) => void;
type HandleChange = (e: ChangeEvent<HTMLInputElement>) => void;
type HandleBlur = (e: FocusEvent<HTMLInputElement>) => void;

const filterApporteur43397 = (c: Contract) => String(c.apporteur1) === '43397';

const renderFormFields = (values: ResilValues, _errors: FieldErrors<ResilValues>, handleChange: HandleChange, handleBlur: HandleBlur, setValue: UseFormSetValue<ResilValues>, canRenounce = false) => (
    <Card>
        <CardHeader className="pb-6 border-b">
            <CardTitle className="text-lg flex items-center gap-2">
                <FileX className="w-5 h-5 text-primary" />
                {canRenounce ? 'Formulaire de renonciation' : 'Formulaire de résiliation'}
            </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Text
                    type="text"
                    name="police"
                    label="Numéro d'adhésion"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.police}
                    setValue={setValue}
                    disabled
                />
                <Text
                    type="text"
                    name="nom"
                    label="Nom du souscripteur"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.nom}
                    setValue={setValue}
                    disabled
                />
            </div>

            {values.prenom && (
                <div className="max-w-md">
                    <Text
                        type="text"
                        name="prenom"
                        label="Prénom du souscripteur"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.prenom}
                        setValue={setValue}
                        disabled
                    />
                </div>
            )}

            <div className="max-w-md">
                <Text
                    type="text"
                    name="email"
                    autoComplete="email"
                    label="Adresse email de contact"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    setValue={setValue}
                />
            </div>
        </CardContent>
    </Card>
);

interface RecapOptions {
    buttonDisabled: boolean;
    showRecontact: boolean;
    setShowRecap: (value: boolean) => void;
    prelevement: unknown;
    canRenounce?: boolean;
}

const renderRecap = (values: ResilValues, setValue: UseFormSetValue<ResilValues>, { buttonDisabled, showRecontact, setShowRecap, prelevement, canRenounce = false }: RecapOptions) => (
    <Card>
        <CardHeader className="pb-6 border-b">
            <CardTitle className="text-lg flex items-center gap-2 text-primary">
                <FileX className="w-5 h-5" />
                Récapitulatif de votre demande
            </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                    <p className="text-sm text-muted-foreground mb-0.5">Nom et prénom</p>
                    <p className="font-medium">
                        {values.nom} {values.prenom}
                    </p>
                </div>
                <div>
                    <p className="text-sm text-muted-foreground mb-0.5">Numéro d'adhésion</p>
                    <p className="font-medium">{values.police}</p>
                </div>
            </div>

            <hr className="border-border" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                    <p className="text-sm text-muted-foreground mb-0.5">{canRenounce ? 'Motif de votre renonciation' : 'Motif de votre résiliation'}</p>
                    <p className="font-medium">
                        {values.motif === 'nvei' ? 'Résiliation contrat NVEI' : getMotif(values.motif)}
                    </p>
                </div>
                {values.date_evenement && (
                    <div>
                        <p className="text-sm text-muted-foreground mb-0.5">{canRenounce ? 'Date d\'effet de votre renonciation' : 'Date d\'effet de votre résiliation'}</p>
                        <p className="font-medium">{formatDate(values.date_evenement)}</p>
                    </div>
                )}
            </div>

            {values.commentaire && (
                <>
                    <hr className="border-border" />
                    <div>
                        <p className="text-sm text-muted-foreground mb-0.5">Commentaire</p>
                        <p className="font-medium">{values.commentaire}</p>
                    </div>
                </>
            )}

            {!prelevement && values.recontact !== '1' && values.motif && showRecontact && (
                <FieldsResilRib setValue={setValue} values={values} />
            )}

            <hr className="border-border" />

            <div className="flex flex-col sm:flex-row gap-3">
                <Button
                    type="submit"
                    disabled={buttonDisabled}
                    className="w-full sm:flex-1 text-white cursor-pointer text-sm whitespace-normal"
                >
                    <Send className="w-4 h-4 shrink-0" />
                    {values.recontact === '1' ? 'Confirmer ma demande de rappel' : canRenounce ? 'Confirmer ma renonciation' : 'Confirmer ma résiliation'}
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    className="w-full sm:flex-1 cursor-pointer"
                    onClick={() => setShowRecap(false)}
                >
                    <ChevronLeft className="w-4 h-4 shrink-0" />
                    Annuler
                </Button>
            </div>
        </CardContent>
    </Card>
);

interface NveiFormProps {
    initialValues: ResilValues;
    postTerminate: PostTerminate;
    prelevement: unknown;
}

const NveiForm = ({ initialValues, postTerminate, prelevement }: NveiFormProps) => {
    const [showRecap, setShowRecap] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const navigate = useNavigate();

    const methods = useForm<ResilValues>({
        defaultValues: { ...initialValues, motif: 'nvei', date_evenement: todayIso(), action: 'terminate' }
    });
    const { handleSubmit, formState: { errors }, setValue, watch } = methods;
    const values = watch();

    const handleChange: HandleChange = (e) => {
        setValue(e.target.name as keyof ResilValues, e.target.value, { shouldValidate: true });
    };
    const handleBlur: HandleBlur = (e) => {
        setValue(e.target.name as keyof ResilValues, e.target.value, { shouldValidate: true });
    };

    const onSubmit = (values: ResilValues) => {
        setButtonDisabled(true);
        postTerminate(values, () => { void navigate('/'); methods.reset(); });
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {!showRecap ? (
                    <>
                        {renderFormFields(values, errors, handleChange, handleBlur, setValue)}

                        <Card>
                            <CardContent className="pt-6 space-y-5">
                                <Alert variant="warning">
                                    <AlertTriangle className="h-4 w-4" />
                                    <AlertDescription>
                                        Votre contrat NVEI sera résilié à compter d'aujourd'hui.
                                    </AlertDescription>
                                </Alert>

                                <SimpleDatePicker
                                    label="Date d'effet de votre résiliation"
                                    name="date_evenement"
                                    setValue={(name, value) => setValue(name as 'date_evenement', value)}
                                    value={values.date_evenement}
                                    error={errors.date_evenement?.message}
                                    required
                                    disabled
                                />

                                <Button type="button" className="w-full text-white cursor-pointer" onClick={() => setShowRecap(true)}>
                                    Je valide
                                </Button>
                            </CardContent>
                        </Card>
                    </>
                ) : (
                    renderRecap(values, setValue, { buttonDisabled, showRecontact: false, setShowRecap, prelevement })
                )}
            </form>
        </FormProvider>
    );
};

interface StandardFormProps {
    initialValues: ResilValues;
    situation?: string;
    dateAfn?: string;
    titre?: string;
    usage?: string;
    dateAvt?: string;
    garanties?: Garantie[];
    accidents?: Accident[];
    postTerminate: PostTerminate;
    dateEcheance?: string;
    prelevement: unknown;
    isVAE: boolean;
    isNVEI: boolean;
    logo?: string;
    bookingFidelisationUrl?: string | null;
}

const StandardForm = ({ initialValues, situation, dateAfn, titre, usage, dateAvt, garanties, accidents, postTerminate, dateEcheance, prelevement, isVAE, isNVEI, logo, bookingFidelisationUrl }: StandardFormProps) => {
    const [requiredFile, setRequiredFile] = useState(false);
    const [msgInfo, setMsgInfo] = useState('');
    const [showRecontact, setShowRecontact] = useState(false);
    const [showRecap, setShowRecap] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const navigate = useNavigate();

    /* Jours calendaires, comme le Pass : à 10 h, l'échéance dans 60 jours compte −60, pas −59. */
    const diffDaysAfnToday = diffInCalendarDays(new Date(), dateAfn);
    const diffDaysAvtToday = diffInCalendarDays(new Date(), dateAvt);
    const diffEcheanceToday = diffInCalendarDays(new Date(), dateEcheance);
    const canRenounce = isVAE && diffDaysAfnToday <= 14;

    const methods = useForm<ResilValues>({
        defaultValues: initialValues
    });
    const { handleSubmit, formState: { errors }, setValue, watch } = methods;
    const values = watch();

    const handleChange: HandleChange = (e) => {
        setValue(e.target.name as keyof ResilValues, e.target.value, { shouldValidate: true });
    };
    const handleBlur: HandleBlur = (e) => {
        setValue(e.target.name as keyof ResilValues, e.target.value, { shouldValidate: true });
    };

    const swalWarning = (text: string) => {
        void Swal.fire({
            title: 'Attention',
            text,
            showConfirmButton: true,
            confirmButtonText: 'Terminer',
            confirmButtonColor: getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim(),
            showCloseButton: true
        });
    };

    const handleClickNextStep = () => {
        if (!values.motif) {
            swalWarning(canRenounce ? 'Merci de sélectionner un motif de renonciation.' : 'Merci de sélectionner un motif de résiliation.');
        } else if (values.motif === 'A' && values.files['B'] === undefined) {
            swalWarning('Merci de nous transmettre le certificat de cession ou la carte barrée et signée.');
        } else if (values.motif === 'vol' && values.files['B'] === undefined) {
            swalWarning("Merci de nous transmettre le procès-verbal de vol. Si vous ne l'avez pas, nous vous invitons à déposer une plainte auprès des forces de l'ordre");
        } else if (values.motif === 'situation' && !values.commentaire) {
            swalWarning('Merci de préciser votre situation en commentaire');
        } else if (values.motif === 'autre' && !values.commentaire) {
            swalWarning('Merci de préciser votre demande en commentaire');
        } else if (values.motif === 'A' && !values.susp_res) {
            swalWarning('Merci de préciser si vous souhaitez suspendre ou résilier votre contrat');
        } else if (['0', 'echeance', 'prime'].includes(values.motif) && isHttpUrl(bookingFidelisationUrl)) {
            void Swal.fire({
                html: sanitizeHtml(`
                    <div class="popup-fidelisation text-center">
                        <img src="${logo}" alt="APRIL Moto" class="popup-fidelisation-logo mb-4" style="max-width: 150px;" />
                        <p class="font-bold" style="color: #104373;">Vous envisagez de nous quitter ?</p>
                        <p class="font-bold mt-4 mb-0" style="color: #104373;">Prenez un instant avant de vous décider...</p>
                        <p class="font-bold" style="color: #104373;">Laissez-nous vous proposer une solution personnalisée ou une offre exclusive qui pourrait vous faire changer d'avis.</p>
                        <a href="${bookingFidelisationUrl}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none bg-primary text-primary-foreground border-none hover:bg-primary-hover mt-4">Je prends rendez-vous</a>
                        <img src="${priseRDV}" alt="Prise de rendez-vous" class="popup-fidelisation-img my-6 block mx-auto" style="max-width: 250px;" />
                        <button type="button" id="btn-confirme-resiliation" class="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-[0.875rem]/5 font-medium rounded-md transition-all ease-[ease] cursor-pointer no-underline disabled:opacity-50 disabled:pointer-events-none btn-outline-secondary">Je confirme la résiliation</button>
                    </div>
                `),
                showConfirmButton: false,
                showCloseButton: true,
                didOpen: () => {
                    const btnConfirme = document.getElementById('btn-confirme-resiliation');
                    if (btnConfirme) {
                        btnConfirme.addEventListener('click', () => {
                            Swal.close();
                            setShowRecap(true);
                            window.scroll(0, 0);
                        });
                    }
                }
            });
        } else {
            setShowRecap(true);
            window.scroll(0, 0);
        }
    };

    const onChangeMotifResil = (value: string) => {
        setMsgInfo('');
        setValue('motif', value, { shouldValidate: true });
        setRequiredFile(false);
        setShowRecontact(false);
        setValue('date_evenement', undefined, { shouldValidate: true });

        const setCommonFields = (action: string, motif_refus: string | null = null) => {
            setValue('action', action, { shouldValidate: true });
            if (motif_refus) {
                setValue('motif_refus', motif_refus, { shouldValidate: true });
            }
        };

        switch (value) {
            case 'echeance':
                if (diffEcheanceToday > 335 || (diffEcheanceToday <= -90 && diffEcheanceToday >= -335)) {
                    setCommonFields('ag_res');
                    setValue('date_ag_res', formatDate(subDays(toDate(dateEcheance), 60), DATE_ISO), { shouldValidate: true });
                } else if (diffEcheanceToday <= -60 && diffEcheanceToday >= -90) {
                    if (!prelevement) {
                        setCommonFields('create_res_esp');
                    } else {
                        setCommonFields('terminate');
                        setValue('date_evenement', formatDate(subDays(toDate(dateEcheance), 1), DATE_ISO), { shouldValidate: true });
                        setValue('motif', 'B', { shouldValidate: true });
                    }
                } else if (diffEcheanceToday <= 0 && diffEcheanceToday >= -60) {
                    setCommonFields('create_res');
                } else if (diffEcheanceToday <= 30) {
                    setShowRecontact(false);
                    setCommonFields('create_res');
                } else {
                    setCommonFields('create_res_esp');
                }
                break;
            case 'Q':
                if (diffDaysAfnToday <= 14) {
                    if (!prelevement) {
                        setCommonFields('create_res_esp');
                    } else {
                        setValue('date_evenement', todayIso(), { shouldValidate: true });
                        setCommonFields('terminate');
                    }
                } else {
                    setCommonFields('send_mail_refus', 'Q.1');
                }
                break;
            case '0': {
                setShowRecontact(true);
                let motif_refus: string;
                if (diffDaysAfnToday < 365) {
                    motif_refus = '0.1';
                } else if (titre === 'SOC' || usage === 'MA - Affaires') {
                    motif_refus = '0.2';
                } else {
                    motif_refus = '0.3';
                }
                setCommonFields('send_mail_refus', motif_refus);
                break;
            }
            case 'A':
                if (!prelevement) {
                    setCommonFields('create_res_esp');
                } else {
                    setValue('action', 'create_res_vente', { shouldValidate: true });
                }
                setRequiredFile(true);
                break;
            case '3':
                if (diffDaysAvtToday <= 30) {
                    if (!prelevement) {
                        setCommonFields('create_res_esp');
                    } else {
                        setValue('date_evenement', formatDate(addDays(new Date(), 10), DATE_ISO), { shouldValidate: true });
                        setCommonFields('terminate');
                    }
                } else {
                    setCommonFields('send_mail_refus', '3.1');
                }
                break;
            case 'prime':
                setCommonFields('create_res_esp');
                break;
            case 'situation':
                setRequiredFile(true);
                setCommonFields('create_res_esp');
                break;
            case 'vol':
                if (!garanties?.find((g) => g.code_famille === 'VI')) {
                    setValue('action', 'create_res_esp', { shouldValidate: true });
                    setRequiredFile(true);
                } else {
                    setRequiredFile(false);
                    if (accidents?.length) {
                        /* L'ancien test `_.includes(accident, ['categorieCode', [...]])` cherchait
                           un tableau neuf parmi les valeurs du sinistre : il était toujours faux. */
                        setMsgInfo('Suite à la déclaration pour vol, votre contrat sera suspendu automatiquement 30 jours après le vol.');
                    } else {
                        setMsgInfo('Vous devez dans un premier temps déclarer le vol sur votre espace assuré, puis votre contrat sera automatiquement suspendu 30 jours après le vol.');
                    }
                }
                break;
            case 'X':
                if (situation === '2' && diffDaysAfnToday < 30) {
                    if (!prelevement) {
                        setCommonFields('create_res_esp');
                    } else {
                        setValue('action', 'terminate', { shouldValidate: true });
                        setValue('date_evenement', formatDate(dateAfn, DATE_ISO), { shouldValidate: true });
                    }
                }
                break;
            case 'autre':
                setRequiredFile(true);
                setCommonFields('create_res_esp');
                break;
            default:
                break;
        }
    };

    const onSubmit = (values: ResilValues) => {
        setButtonDisabled(true);
        postTerminate(values, () => { void navigate('/'); methods.reset(); });
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {!showRecap ? (
                    <>
                        {renderFormFields(values, errors, handleChange, handleBlur, setValue, canRenounce)}

                        <Card>
                            <CardContent className="pt-6 space-y-5">
                                <Select
                                    required
                                    name="motif"
                                    value={values.motif}
                                    error={errors.motif?.message}
                                    label={canRenounce ? 'Quel est le motif de votre renonciation ?' : 'Quel est le motif de votre résiliation ?'}
                                    onChange={e => onChangeMotifResil(e.target.value)}
                                >
                                    <option value="">Sélectionnez votre motif</option>
                                    <option value="Q">Résiliation rétractation 14 jours</option>
                                    <option value="vol">Résiliation pour vol de véhicule</option>
                                    {situation === '2' && <option value="X">Résiliation pour non achat</option>}
                                    <option value="A">Résiliation suite à vente</option>
                                    {situation !== '2' && (
                                        <>
                                            <option value="echeance">Résiliation à échéance / loi Chatel</option>
                                            {!isVAE && !isNVEI && <option value="0">Résiliation Loi Hamon</option>}
                                            <option value="3">Résiliation suite à un refus d'avenant</option>
                                            <option value="prime">Résiliation pour augmentation de prime</option>
                                            <option value="situation">Résiliation pour changement de situation (adresse, profession...)</option>
                                            <option value="autre">Résiliation pour un autre motif</option>
                                        </>
                                    )}
                                </Select>

                                {values.date_evenement && (
                                    <>
                                        {values.motif === '3' && (
                                            <Alert variant="warning">
                                                <AlertTriangle className="h-4 w-4" />
                                                <AlertDescription>
                                                    Lors d'un refus d'avenant, la résiliation est effective 10 jours après la demande.
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                        <SimpleDatePicker
                                            label={canRenounce ? "Date de l'évènement donnant lieu à la renonciation" : "Date de l'évènement donnant lieu à la résiliation"}
                                            name="date_evenement"
                                            setValue={(name, value) => setValue(name as 'date_evenement', value)}
                                            value={values.date_evenement}
                                            error={errors.date_evenement?.message}
                                            required
                                            disabled
                                        />
                                    </>
                                )}

                                {values.motif === 'A' && (
                                    <RadioGroup
                                        label="Souhaitez vous suspendre ou résilier votre contrat ?"
                                        name="susp_res"
                                        setValue={setValue}
                                        options={[
                                            { value: "suspension", label: "Suspendre" },
                                            { value: "resiliation", label: "Résilier" },
                                        ]}
                                        value={values.susp_res}
                                        required={values.motif === 'A'}
                                    />
                                )}

                                {showRecontact && (
                                    <RadioGroup
                                        label={values.motif === '0' ? "Vous voulez nous quitter ? Et si nous prenions 5 minutes pour revoir ensemble votre contrat ?" : values.motif === 'prime' ? "Votre contrat est-il toujours adapté ? Nous pouvons revoir vos besoins ensemble" : "Il est encore temps de revoir votre contrat avant la prochaine échéance, et si nous en discutions ?"}
                                        name="recontact"
                                        setValue={setValue}
                                        options={[
                                            { value: "1", label: "Oui" },
                                            { value: "0", label: "Non" },
                                        ]}
                                        value={values.recontact}
                                    />
                                )}

                                {(values.motif === 'situation' || values.motif === 'autre') && (
                                    <Textarea
                                        label="Commentaire"
                                        name="commentaire"
                                        value={values.commentaire}
                                        error={errors.commentaire?.message}
                                        maxLength={255}
                                    />
                                )}

                                {requiredFile && (
                                    <div className="space-y-4">
                                        {values.motif === 'vol' && (
                                            <Alert variant="warning">
                                                <AlertTriangle className="h-4 w-4" />
                                                <AlertDescription>
                                                    Merci de nous transmettre le procès-verbal de vol. Si vous ne l'avez pas, nous vous invitons à déposer une plainte auprès des forces de l'ordre.
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                        {values.motif === 'A' && (
                                            <Alert variant="warning">
                                                <AlertTriangle className="h-4 w-4" />
                                                <AlertDescription>
                                                    Pour que votre résiliation soit prise en compte, seuls un certificat de cession ou une carte grise barrée seront acceptés.
                                                </AlertDescription>
                                            </Alert>
                                        )}
                                        <Files
                                            label={values.motif === 'A' ? 'Certificat de cession / Carte grise barrée' : 'Pièce justificative'}
                                            name={`files[B]`}
                                            arrayName={'files'}
                                            value={values.files}
                                            setValue={setValue}
                                            code={'B'}
                                            accept={'image/*,application/pdf'}
                                        />
                                    </div>
                                )}

                                {!prelevement && values.motif && values.motif !== 'vol' && !showRecontact && (
                                    <FieldsResilRib setValue={setValue} values={values} />
                                )}

                                {msgInfo ? (
                                    <Warning messageWarning={msgInfo} />
                                ) : (
                                    <Button type="button" className="w-full text-white cursor-pointer" onClick={handleClickNextStep}>
                                        Je valide
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </>
                ) : (
                    renderRecap(values, setValue, { buttonDisabled, showRecontact, setShowRecap, prelevement, canRenounce })
                )}
            </form>
        </FormProvider>
    );
};

const FormulaireResil = () => {
    const contract = useSelectedContract();
    const contrat_id = contract?.id;
    const souscripteur = contract?.souscripteur;
    const situationContrat = contract?.situation;
    const isVAE = isVAEContract(contract);
    const isNVEI = isNVEIContract(contract);
    const situation = situationContrat?.statutCode;
    const dateEcheance = situationContrat?.dateEcheance;
    const dateAfn = situationContrat?.dateAffaireNouvelle;
    const titre = souscripteur?.titre;
    const usage = contract?.vehicule?.usage;
    const dateAvt = situationContrat?.date;
    const garanties = getContractGarantiesOptions(contract);
    // L'ancien container appelait `fetchAccidents(query)` au lieu de `fetchAccidents({ query })` :
    // les sinistres n'étaient jamais chargés. On conserve ce comportement observable (liste vide).
    const accidents: Accident[] = [];
    const prelevement = contract?.prelevement;
    const logo = useWhitelabel().data?.logo_light_url;
    const bookingFidelisationUrl = useBookingFidelisationUrl(contrat_id).data;
    const initialValues: ResilValues = {
        police: contract?.numeroPolice,
        adhesion: contract?.adhesionPrincipale,
        prenom: souscripteur?.firstname,
        nom: souscripteur?.lastname,
        email: souscripteur?.email,
        files: {},
    };
    const terminate = useTerminate();
    const postTerminate: PostTerminate = (values, successFunction) =>
        terminate.mutate({ contractId: contrat_id as string | number, form: values as unknown as TerminateForm, successFunction });

    const diffDaysAfnToday = diffInCalendarDays(new Date(), dateAfn);
    const canRenounce = isVAE && diffDaysAfnToday <= 14;

    return (
        <div className="space-y-6">
            {!['6', '5', '9'].includes(situation ?? '') && (
                <div className="rounded-xl bg-primary p-6 text-white">
                    <div className="flex items-start gap-3">
                        <Phone className="h-5 w-5 shrink-0 mt-0.5" />
                        <div>
                            <p>{canRenounce ? 'Avant de renoncer à votre contrat' : 'Avant de résilier votre contrat'}, prenons le temps d'échanger au <strong>02 47 51 07 07</strong> pour trouver ensemble une solution adaptée à votre situation.</p>
                            <span className="block text-xs opacity-80 mt-1">(Appel non surtaxé du lundi au vendredi de 9h à 18h)</span>
                        </div>
                    </div>
                </div>
            )}

            {/* `key` : le formulaire ne lit `initialValues` qu'à son montage. SelectContract ne le
                remonte pas quand le contrat choisi est déjà en cache : sans elle, il garderait la
                police, le nom et les pièces jointes du contrat précédent. */}
            <SelectContract filterFn={filterApporteur43397}>
                {['6', '5', '9', 'I'].includes(situation ?? '') ? (
                    <Warning messageWarning={'Votre contrat est déjà résilié ou suspendu.'} />
                ) : isNVEI && diffDaysAfnToday < 365 ? (
                    <Warning messageWarning={"Le contrat a moins d'un an. La résiliation n'est pas possible"} />
                ) : isNVEI && diffDaysAfnToday >= 365 ? (
                    <NveiForm
                        key={contrat_id}
                        initialValues={initialValues}
                        postTerminate={postTerminate}
                        prelevement={prelevement}
                    />
                ) : (
                    <StandardForm
                        key={contrat_id}
                        initialValues={initialValues}
                        situation={situation}
                        dateAfn={dateAfn}
                        titre={titre}
                        usage={usage}
                        dateAvt={dateAvt}
                        garanties={garanties}
                        accidents={accidents}
                        postTerminate={postTerminate}
                        dateEcheance={dateEcheance}
                        prelevement={prelevement}
                        isVAE={isVAE}
                        isNVEI={isNVEI}
                        logo={logo}
                        bookingFidelisationUrl={bookingFidelisationUrl}
                    />
                )}
            </SelectContract>
        </div>
    );
};

export default FormulaireResil;
